import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import { MdCloudUpload } from "react-icons/md";
import useFileUpload from "hooks/useFileUpload";
import useCrud from "hooks/useCrud";
import { INCOME_ENTRY_STATUS, STATUS_CODES } from "constants/index";
import { INCOME_ENTRIES_URL } from "constants/urls";
import type { IncomeEntryCreationResult } from "types";

type CreationState = {
  status: "idle" | "creating" | "success" | "error";
  result: IncomeEntryCreationResult | null;
  error: string | null;
};

type UploadAttempt = { creating: boolean };

type Props = { onCreated?: (result: IncomeEntryCreationResult) => void };

const INITIAL_CREATION: CreationState = { status: "idle", result: null, error: null };

export default function IncomeEntryFileUpload({ onCreated }: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [creation, setCreation] = useState<CreationState>(INITIAL_CREATION);
  const attemptRef = useRef<UploadAttempt | null>(null);
  const { upload, reset, status, progress, error, uploadedFile } = useFileUpload();
  const { createData } = useCrud<{ pdfFileId: number }>(INCOME_ENTRIES_URL, {
    disableAutoFetch: true,
  });
  const isUploading = status === "requesting" || status === "uploading" || status === "confirming";
  const isCreating = creation.status === "creating";
  const isBusy = isUploading || isCreating;
  const fileError = selectedFile && selectedFile.type !== "application/pdf"
    ? "Seleccione un archivo PDF para cargar el remito."
    : error;

  useEffect(() => () => {
    attemptRef.current = null;
  }, []);

  function isCurrent(attempt: UploadAttempt) {
    return attemptRef.current === attempt;
  }

  async function createPreincome(pdfFileId: number, attempt: UploadAttempt) {
    let created: IncomeEntryCreationResult | undefined;
    attempt.creating = true;
    setCreation({ status: "creating", result: null, error: null });
    try {
      if (!Number.isSafeInteger(pdfFileId) || pdfFileId <= 0) {
        throw new Error("El identificador del archivo no es valido.");
      }
      const response = await createData({ pdfFileId }, true);
      if (!isCurrent(attempt)) return;
      if (response?.status !== STATUS_CODES.OK) {
        const message = response?.data?.message;
        throw new Error(typeof message === "string" && message.trim() ? message : "No se pudo crear el preingreso.");
      }
      const result: IncomeEntryCreationResult | undefined = response.data;
      if (!result || !Number.isSafeInteger(result.id) || result.id <= 0 || result.pdfFileId !== pdfFileId ||
        typeof result.numero_remito !== "string" || !result.numero_remito.trim() ||
        !Object.values(INCOME_ENTRY_STATUS).includes(result.status)) {
        throw new Error("La API devolvio una respuesta de creacion de preingreso invalida.");
      }
      if (isCurrent(attempt)) {
        setCreation({ status: "success", result, error: null });
        created = result;
      }
    } catch (error) {
      if (isCurrent(attempt)) {
        setCreation({
          status: "error",
          result: null,
          error: error instanceof Error ? error.message : "No se pudo crear el preingreso.",
        });
      }
    } finally {
      if (attemptRef.current === attempt) attemptRef.current = null;
    }
    if (created) onCreated?.(created);
  }

  async function handleSelect(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file || attemptRef.current) return;

    reset();
    setCreation(INITIAL_CREATION);
    setSelectedFile(file);
    if (file.type !== "application/pdf") return;

    const attempt: UploadAttempt = { creating: false };
    attemptRef.current = attempt;
    const confirmed = await upload(file);
    if (!isCurrent(attempt)) return;
    if (!confirmed) {
      attemptRef.current = null;
      return;
    }
    await createPreincome(confirmed.fileId, attempt);
  }

  function handleRetryCreation() {
    if (!uploadedFile || attemptRef.current) return;
    const attempt: UploadAttempt = { creating: false };
    attemptRef.current = attempt;
    void createPreincome(uploadedFile.fileId, attempt);
  }

  function handleClose() {
    // Closing the dialog cannot undo a creation already processed by the API.
    if (attemptRef.current?.creating) return;
    attemptRef.current = null;
    reset();
    setCreation(INITIAL_CREATION);
    setSelectedFile(null);
  }

  return (
    <>
      <Button component="label" variant="contained" startIcon={<MdCloudUpload />} disabled={isBusy}>
        Cargar Archivo
        <input
          type="file"
          accept="application/pdf"
          aria-label="Seleccionar remito PDF"
          hidden
          disabled={isBusy}
          onChange={handleSelect}
        />
      </Button>
      <Dialog open={selectedFile !== null} onClose={handleClose} disableEscapeKeyDown={isCreating} fullWidth maxWidth="sm" aria-labelledby="income-entry-upload-title">
        <DialogTitle id="income-entry-upload-title">Cargar remito PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, overflowWrap: "anywhere" }}>
            {selectedFile?.name}
          </Typography>
          {isBusy && (
            <Box role="status" aria-live="polite" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {isCreating ? "Creando preingreso..." : status === "requesting" ? "Solicitando permiso de carga..." :
                  status === "confirming" ? "Verificando archivo..." : `Subiendo archivo: ${progress}%`}
              </Typography>
              <LinearProgress
                aria-label="Progreso de carga"
                variant={!isCreating && status === "uploading" ? "determinate" : "indeterminate"}
                value={progress}
              />
            </Box>
          )}
          {fileError && <Alert severity="error">{fileError}</Alert>}
          {creation.error && (
            <Alert severity="error">
              {creation.error} El archivo ya esta confirmado; puede reintentar la creacion sin subirlo nuevamente.
            </Alert>
          )}
          {creation.result && (
            <Alert severity="success">
              Preingreso #{creation.result.id} guardado correctamente. Remito {creation.result.numero_remito}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isCreating}>Cerrar</Button>
          {creation.status === "error" && uploadedFile && (
            <Button variant="contained" onClick={handleRetryCreation}>Reintentar creacion</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
